/**
 * Wires `registerStudio.model.ts` to the dialog that registers a studio work.
 *
 * Calls `registerStudioWork` for the work and then `uploadWorkImages` for the
 * photographs, in that order because the bytes need a work id to attach to;
 * a failed upload leaves a registered work with a notice, never a half-made
 * one. `onRegistered` is how the studio book learns to re-read itself.
 *
 * The state the model cannot hold is the open flag, the stage, the images
 * with their preview URLs, the intake refusals, and the busy/error/notice
 * trio. Closing puts the draft down and cancel is the one control that
 * discards it — until the work is registered, after which closing resets so
 * a second press does not re-offer the same title.
 */

import { useEffect, useRef, useState } from "react";
import type { ImageKind } from "@ondo/shared";

import { ApiError, registerStudioWork } from "../lib/api.ts";
import { uploadWorkImages } from "../lib/workImages.ts";
import { nextKind, screenImages } from "../register/register.model.ts";
import type { PendingImage } from "../register/register.model.ts";
import { STUDIO_REGISTER_COPY } from "./estate.constants.ts";
import {
  EMPTY_STUDIO_DRAFT,
  confirmRows,
  detailsRefusal,
  stepIndex,
  toStudioRequest,
} from "./registerStudio.model.ts";
import type {
  ConfirmRow,
  StudioRegisterDraft,
  StudioRegisterStage,
} from "./registerStudio.model.ts";

/** What the dialog renders from. */
export interface StudioRegisterView {
  open: boolean;
  stage: StudioRegisterStage;
  /** 0-based, for the step strip; equals the step count once registered. */
  step: number;
  draft: StudioRegisterDraft;
  images: PendingImage[];
  /** One sentence per file refused at intake. */
  refusals: string[];
  rows: ConfirmRow[];
  busy: boolean;
  /** The reason the current step cannot proceed, or the server's refusal. */
  error: string | null;
  /** A notice on the registered panel — images that failed after the work exists. */
  notice: string | null;
}

/**
 * The register dialog's state: one draft, one stage, the images in memory.
 *
 * CLOSING DOES NOT DISCARD, as `AddHistoryDialog` argues: Escape and ✕ put
 * the draft down and the button picks it back up. Cancel is the one control
 * that throws it away. Once registered, closing resets — the draft has done
 * its work and a second press should not re-offer the same title.
 *
 * THE WORK FIRST, THEN THE BYTES. The photographs need a work id to attach
 * to, so they upload after the API answers — and a failure there leaves a
 * registered work with a notice, never a half-made one: the transaction
 * behind the one call is what makes that true.
 */
export function useRegisterStudioController(
  studioOndoId: string,
  onRegistered: () => void,
) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<StudioRegisterStage>({ kind: "details" });
  const [draft, setDraft] = useState<StudioRegisterDraft>(EMPTY_STUDIO_DRAFT);
  const [images, setImages] = useState<PendingImage[]>([]);
  const [refusals, setRefusals] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const previewUrls = useRef<string[]>([]);

  // Object URLs are browser memory; freed once when the screen goes.
  useEffect(
    () => () => {
      for (const url of previewUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const reset = () => {
    for (const url of previewUrls.current) URL.revokeObjectURL(url);
    previewUrls.current = [];
    setStage({ kind: "details" });
    setDraft(EMPTY_STUDIO_DRAFT);
    setImages([]);
    setRefusals([]);
    setError(null);
    setNotice(null);
  };

  const edit = (patch: Partial<StudioRegisterDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setError(null);
  };

  const next = () => {
    setError(null);
    if (stage.kind === "details") {
      const stop = detailsRefusal(draft);
      if (stop !== null) {
        setError(stop);
        return;
      }
      setStage({ kind: "images" });
    } else if (stage.kind === "images") setStage({ kind: "provenance" });
    else if (stage.kind === "provenance") setStage({ kind: "confirm" });
  };

  const back = () => {
    setError(null);
    if (stage.kind === "images") setStage({ kind: "details" });
    else if (stage.kind === "provenance") setStage({ kind: "images" });
    else if (stage.kind === "confirm") setStage({ kind: "provenance" });
  };

  const addImages = (files: File[]) => {
    const intake = screenImages(files, images);
    setRefusals(intake.rejected);
    if (intake.accepted.length === 0) return;

    setImages((current) => {
      let all = current;
      for (const file of intake.accepted) {
        const url = URL.createObjectURL(file);
        previewUrls.current.push(url);
        all = [...all, { id: crypto.randomUUID(), file, kind: nextKind(all), previewUrl: url }];
      }
      return all;
    });
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const going = current.find((image) => image.id === id);
      if (going) URL.revokeObjectURL(going.previewUrl);
      return current.filter((image) => image.id !== id);
    });
  };

  const setImageKind = (id: string, kind: ImageKind) => {
    setImages((current) =>
      current.map((image) => (image.id === id ? { ...image, kind } : image)),
    );
  };

  const submit = async () => {
    if (busy || stage.kind !== "confirm") return;

    const stop = detailsRefusal(draft);
    if (stop !== null) {
      setError(stop);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const work = await registerStudioWork(toStudioRequest(draft, studioOndoId));

      let failed = 0;
      if (images.length > 0) {
        try {
          await uploadWorkImages(work.workId, images);
        } catch {
          failed = images.length;
        }
      }

      setNotice(failed > 0 ? STUDIO_REGISTER_COPY.imagesFailed(failed) : null);
      setStage({ kind: "registered", ...work });
      onRegistered();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : STUDIO_REGISTER_COPY.failed);
    } finally {
      setBusy(false);
    }
  };

  const view: StudioRegisterView = {
    open,
    stage,
    step: stepIndex(stage),
    draft,
    images,
    refusals,
    rows: confirmRows(draft, images),
    busy,
    error,
    notice,
  };

  return {
    view,
    openDialog: () => {
      if (stage.kind === "registered") reset();
      setOpen(true);
    },
    /** Puts the draft down. Escape, the backdrop, ✕, and Done. */
    closeDialog: () => {
      setOpen(false);
      if (stage.kind === "registered") reset();
    },
    /** Discards it. The only control that does. */
    cancel: () => {
      reset();
      setOpen(false);
    },
    registerAnother: reset,
    edit,
    next,
    back,
    addImages,
    removeImage,
    setImageKind,
    submit,
  };
}
